import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue } | any;

interface StoredEnvelope<T> {
	value: T;
	expiresAt?: number; // epoch ms
}

@Injectable({
	providedIn: 'root'
})
export class AppStorageService {

	getItem<T extends JsonValue = JsonValue>(key: string): T | null {
		const raw = localStorage.getItem(key);
		if (raw == null) return null;
		try {
			const parsed = JSON.parse(raw) as StoredEnvelope<T> | T;
			if (this.isEnvelope(parsed)) {
				if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
					localStorage.removeItem(key);
					return null;
				}
				return parsed.value;
			}
			return parsed as T;
		} catch {
			return raw as unknown as T; // fallback for legacy plain strings
		}
	}

	setItem<T extends JsonValue = JsonValue>(key: string, value: T, ttlMs?: number): void {
		const envelope: StoredEnvelope<T> = {
			value,
			expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
		};
		localStorage.setItem(key, JSON.stringify(envelope));
	}

	removeItem(key: string): void {
		localStorage.removeItem(key);
	}

	clear(): void {
		localStorage.clear();
	}

	has(key: string): boolean {
		const raw = localStorage.getItem(key);
		if (raw == null) return false;
		try {
			const parsed = JSON.parse(raw) as StoredEnvelope<JsonValue> | JsonValue;
			if (this.isEnvelope(parsed)) {
				if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
					localStorage.removeItem(key);
					return false;
				}
			}
			return true;
		} catch {
			return true;
		}
	}

	private isEnvelope<T>(obj: unknown): obj is StoredEnvelope<T> {
		return !!obj && typeof obj === 'object' && 'value' in (obj as any);
	}

	// ===== Encryption (AES-GCM, Web Crypto) =====
	async setEncrypted<T extends JsonValue = JsonValue>(key: string, value: T, password: string, ttlMs?: number): Promise<void> {
		const envelope: StoredEnvelope<T> = {
			value,
			expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
		};
		const plaintext = new TextEncoder().encode(JSON.stringify(envelope));
		const salt = this.getSalt();
		const iv = crypto.getRandomValues(new Uint8Array(12));
		const aesKey = await this.deriveAesGcmKey(password, salt, environment.encryption?.iterations || 150000);
		const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext));
		const payload = this.concatArrays(iv, ciphertext);
		localStorage.setItem(key, this.toBase64(payload));
	}

	async getEncrypted<T extends JsonValue = JsonValue>(key: string, password: string): Promise<T | null> {
		const raw = localStorage.getItem(key);
		if (raw == null) return null;
		try {
			const data = this.fromBase64(raw);
			if (data.byteLength < 13) return null;
			const iv = data.slice(0, 12);
			const ciphertext = data.slice(12);
			const salt = this.getSalt();
			const aesKey = await this.deriveAesGcmKey(password, salt, environment.encryption?.iterations || 150000);
			const plaintext = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ciphertext));
			const envelope = JSON.parse(new TextDecoder().decode(plaintext)) as StoredEnvelope<T> | T;
			if (this.isEnvelope(envelope)) {
				if (envelope.expiresAt && Date.now() > envelope.expiresAt) {
					localStorage.removeItem(key);
					return null;
				}
				return envelope.value as T;
			}
			return envelope as T;
		} catch {
			return null;
		}
	}

	private async deriveAesGcmKey(password: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
		const enc = new TextEncoder();
		const baseKey = await crypto.subtle.importKey('raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']);
		return crypto.subtle.deriveKey(
			{ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
			baseKey,
			{ name: 'AES-GCM', length: 256 },
			false,
			['encrypt', 'decrypt']
		);
	}

	private getSalt(): Uint8Array {
		const b64 = environment.encryption?.saltB64 || '';
		if (!b64) {
			// Generate and cache a salt in localStorage if not configured.
			const existing = localStorage.getItem('__app_salt');
			if (existing) return this.fromBase64(existing);
			const salt = crypto.getRandomValues(new Uint8Array(16));
			localStorage.setItem('__app_salt', this.toBase64(salt));
			return salt;
		}
		return this.fromBase64(b64);
	}

	private concatArrays(a: Uint8Array, b: Uint8Array): Uint8Array {
		const out = new Uint8Array(a.length + b.length);
		out.set(a, 0);
		out.set(b, a.length);
		return out;
	}

	private toBase64(bytes: Uint8Array): string {
		let binary = '';
		bytes.forEach((b) => (binary += String.fromCharCode(b)));
		return btoa(binary);
	}

	private fromBase64(b64: string): Uint8Array {
		const binary = atob(b64);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
		return bytes;
	}
}
